import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ApartmentIcon from '@quintoandar/cozy-icons/Apartment';
import CalendarEventIcon from '@quintoandar/cozy-icons/CalendarEvent';
import CloseIcon from '@quintoandar/cozy-icons/Close';
import CopyIcon from '@quintoandar/cozy-icons/Copy';
import OfferIcon from '@quintoandar/cozy-icons/Offer';
import TaskIcon from '@quintoandar/cozy-icons/Task';

import { buttonStyle, colors, styles, tagStyle } from './styles';
import { getTagKind, money } from './formatters';

export function ButtonLink({ children, href, onClick, type, variant }) {
  const style = buttonStyle(variant);
  const content = <span style={{ alignItems: 'center', display: 'inline-flex', gap: 8, minWidth: 0 }}>{children}</span>;

  if (href) {
    return (
      <a href={href} onClick={onClick} style={style}>
        {content}
      </a>
    );
  }

  return (
    <button type={type || 'button'} onClick={onClick} style={style}>
      {content}
    </button>
  );
}

ButtonLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.string,
  variant: PropTypes.string,
};

export function Card({ children, padding = 20, style }) {
  return <section style={{ ...styles.card, padding, ...style }}>{children}</section>;
}

Card.propTypes = {
  children: PropTypes.node.isRequired,
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.object,
};

export function PageIntro({ actions, eyebrow, subtitle, title }) {
  return (
    <header style={styles.intro}>
      {eyebrow ? <p style={{ ...styles.caption, fontWeight: 800 }}>{eyebrow}</p> : null}
      <div style={styles.introContent}>
        <div style={{ minWidth: 0 }}>
          <h1 style={styles.title}>{title}</h1>
          {subtitle ? <p style={styles.subtitle}>{subtitle}</p> : null}
        </div>
        {actions ? <div style={styles.row}>{actions}</div> : null}
      </div>
    </header>
  );
}

PageIntro.propTypes = {
  actions: PropTypes.node,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.node,
  title: PropTypes.node.isRequired,
};

export function Section({ actions, children, subtitle, title }) {
  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <div style={{ minWidth: 0 }}>
          <h2 style={styles.sectionTitle}>{title}</h2>
          {subtitle ? <p style={styles.caption}>{subtitle}</p> : null}
        </div>
        {actions ? <div style={styles.row}>{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

Section.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  title: PropTypes.node.isRequired,
};

export function Tag({ children, kind }) {
  return <span style={tagStyle(kind || getTagKind(String(children)))}>{children}</span>;
}

Tag.propTypes = {
  children: PropTypes.node.isRequired,
  kind: PropTypes.string,
};

export function Alert({ children, kind = 'warning', title }) {
  const palette = {
    critical: [colors.dangerSoft, colors.danger, colors.danger],
    info: [colors.infoSoft, colors.info, colors.info],
    success: [colors.successSoft, colors.success, colors.success],
    warning: [colors.warningSoft, colors.warning, colors.warning],
  }[kind];

  return (
    <div
      style={{
        alignItems: 'flex-start',
        background: palette[0],
        border: `1px solid ${palette[2]}`,
        borderRadius: 8,
        color: colors.text,
        display: 'grid',
        gap: 12,
        gridTemplateColumns: '24px minmax(0, 1fr)',
        padding: 16,
      }}
    >
      <span aria-hidden="true" style={{ color: palette[1], display: 'inline-flex', justifyContent: 'center', marginTop: 1 }}>
        {kind === 'success' ? '✓' : kind === 'info' ? 'i' : '!'}
      </span>
      <div>
        {title ? <strong style={{ color: colors.text, display: 'block', fontSize: 14, lineHeight: 1.45, marginBottom: 4 }}>{title}</strong> : null}
        <div style={{ color: colors.text, fontSize: 14, lineHeight: 1.5 }}>{children}</div>
      </div>
    </div>
  );
}

Alert.propTypes = {
  children: PropTypes.node.isRequired,
  kind: PropTypes.string,
  title: PropTypes.string,
};

export function Field({
  as,
  helper,
  label,
  name,
  onChange,
  options,
  placeholder,
  rows,
  type,
  value,
}) {
  const Component = as === 'textarea' ? 'textarea' : as === 'select' ? 'select' : 'input';
  const fieldStyle =
    as === 'textarea'
      ? { ...styles.field, lineHeight: 1.45, minHeight: rows ? rows * 26 : 104, padding: 12, resize: 'vertical' }
      : styles.field;

  return (
    <label style={{ display: 'block' }}>
      <span style={styles.label}>{label}</span>
      {Component === 'input' ? (
        <input
          name={name}
          onChange={onChange || (() => {})}
          placeholder={placeholder}
          style={fieldStyle}
          type={type || 'text'}
          value={value || ''}
        />
      ) : (
        <Component
          name={name}
          onChange={onChange || (() => {})}
          placeholder={placeholder}
          rows={rows}
          style={fieldStyle}
          value={value || ''}
        >
          {options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Component>
      )}
      {helper ? <span style={styles.helper}>{helper}</span> : null}
    </label>
  );
}

Field.propTypes = {
  as: PropTypes.string,
  helper: PropTypes.node,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export function CurrencyField({ helper, label, value }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={styles.label}>{label}</span>
      <span
        style={{
          border: `1px solid ${colors.borderStrong}`,
          borderRadius: 4,
          display: 'grid',
          gridTemplateColumns: '46px minmax(0, 1fr)',
          minHeight: 42,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            alignItems: 'center',
            background: colors.surfaceAlt,
            borderRight: `1px solid ${colors.border}`,
            color: colors.muted,
            display: 'flex',
            fontWeight: 800,
            justifyContent: 'center',
          }}
        >
          R$
        </span>
        <input inputMode="numeric" style={{ ...styles.field, border: 0, borderRadius: 0 }} value={value} readOnly />
      </span>
      {helper ? <span style={styles.helper}>{helper}</span> : null}
    </label>
  );
}

CurrencyField.propTypes = {
  helper: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export function Tabs({ active, items, onChange, size = 'large' }) {
  return (
    <div
      role="tablist"
      style={{
        alignItems: 'center',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0,
        maxWidth: '100%',
      }}
    >
      {items.map(([id, label, badge]) => {
        const selected = id === active;
        return (
          <button
            aria-selected={selected}
            key={id}
            onClick={() => onChange(id)}
            role="tab"
            style={{
              background: 'transparent',
              border: 0,
              borderBottom: `3px solid ${selected ? colors.primary : 'transparent'}`,
              color: selected ? colors.text : colors.muted,
              cursor: 'pointer',
              flex: '0 0 auto',
              font: 'inherit',
              fontSize: size === 'small' ? 13 : 14,
              fontWeight: 800,
              minHeight: size === 'small' ? 34 : 40,
              marginBottom: -1,
              padding: size === 'small' ? '0 12px' : '0 18px',
            }}
            type="button"
          >
            {label}
            {badge != null ? (
              <span style={{ marginLeft: 8, opacity: selected ? 0.9 : 0.65 }}>{badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

Tabs.propTypes = {
  active: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
};

export function DataTable({ columns, rows, rowKey = 'id' }) {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const query = window.matchMedia('(max-width: 640px)');
    const sync = () => setIsCompact(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  if (isCompact) {
    return (
      <div style={{ ...styles.tableWrap, overflowX: 'auto' }}>
        <table style={{ ...styles.table, minWidth: Math.max(720, columns.length * 180) }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ ...styles.th, width: column.width }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[rowKey]}>
                {columns.map((column) => (
                  <td key={column.key} style={styles.td}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={{ ...styles.th, width: column.width }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[rowKey]}>
              {columns.map((column) => (
                <td key={column.key} style={styles.td}>
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rowKey: PropTypes.string,
  rows: PropTypes.array.isRequired,
};

export function TaskCard({ card }) {
  const highlighted = card.tone === 'highlight';
  const icons = {
    OFFERS: OfferIcon,
    REGISTER: TaskIcon,
    STATUS_PENDING: ApartmentIcon,
    STATUS_PUBLISHED: ApartmentIcon,
    VISITS: CalendarEventIcon,
  };
  const CardIcon = icons[card.id] || TaskIcon;

  return (
    <a
      data-testid={`task-card-${card.id}`}
      href={card.href}
      style={{
        ...styles.card,
        background: highlighted ? colors.surfaceHighlight : colors.surface,
        color: colors.text,
        cursor: 'pointer',
        display: 'block',
        flex: '1 1 230px',
        minHeight: 154,
        minWidth: 230,
        padding: 0,
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          alignItems: 'stretch',
          background: highlighted ? colors.surfaceHighlight : 'transparent',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 154,
          minWidth: 230,
          padding: 16,
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <IconBox active={highlighted}>
            <CardIcon size="medium" />
          </IconBox>
          {card.count != null ? <Tag kind={card.tone}>{card.count}</Tag> : null}
        </div>
        <div>
          <strong style={{ display: 'block', fontSize: 18, lineHeight: 1.25 }}>{card.title}</strong>
          <span style={{ color: highlighted || card.count ? colors.primary : colors.muted, display: 'block', fontSize: 18, fontWeight: 800, lineHeight: 1.25, marginTop: 6 }}>
            {card.subtitle}
          </span>
        </div>
      </div>
    </a>
  );
}

TaskCard.propTypes = {
  card: PropTypes.object.isRequired,
};

export function IconBox({ active, children }) {
  return (
    <span
      aria-hidden="true"
      style={{
        alignItems: 'center',
        background: 'transparent',
        borderRadius: 999,
        color: active ? colors.primary : colors.lighter,
        display: 'inline-flex',
        fontSize: 14,
        fontWeight: 900,
        height: 32,
        justifyContent: 'center',
        width: 32,
      }}
    >
      {children}
    </span>
  );
}

IconBox.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export function PropertyCell({ property, suppressLink }) {
  const href = `/property/${property.id}/summary`;
  const details = `${property.propertyType} · ${property.area} m² · ${property.bedrooms} quartos`;

  return (
    <div style={styles.propertyCell}>
      <div style={{ alignItems: 'center', display: 'flex', gap: 4, minHeight: 22 }}>
        <span style={{ color: colors.text, fontSize: 14, lineHeight: 1.45 }}>{property.crmId || property.id}</span>
        <button
          aria-label="Copiar"
          type="button"
          style={{
            ...styles.iconButton,
            color: colors.primary,
          }}
        >
          <CopyIcon size="small" />
        </button>
      </div>
      {suppressLink ? (
        <span style={styles.propertyText}>{property.address || 'Sem informações do endereço'}</span>
      ) : (
        <a href={href} style={styles.cozyLink}>{property.address || 'Sem informações do endereço'}</a>
      )}
      {suppressLink ? (
        <span style={styles.propertyText}>{details}</span>
      ) : (
        <a href={href} style={styles.cozyLink}>{details}</a>
      )}
    </div>
  );
}

PropertyCell.propTypes = {
  property: PropTypes.object.isRequired,
  suppressLink: PropTypes.bool,
};

export function FeedbackBlock({ action, kind = 'empty-state', subtitle, title }) {
  const icon = kind === 'critical' ? '!' : kind === 'positive' ? '✓' : 'i';
  return (
    <Card style={{ alignItems: 'center', display: 'grid', gap: 10, justifyItems: 'center', padding: 28, textAlign: 'center' }}>
      <IconBox active={kind === 'positive'}>{icon}</IconBox>
      <strong style={{ fontSize: 18 }}>{title}</strong>
      {subtitle ? <p style={{ ...styles.caption, maxWidth: 420 }}>{subtitle}</p> : null}
      {action ? <div style={{ marginTop: 4 }}>{action}</div> : null}
    </Card>
  );
}

FeedbackBlock.propTypes = {
  action: PropTypes.node,
  kind: PropTypes.string,
  subtitle: PropTypes.node,
  title: PropTypes.node.isRequired,
};

export function Modal({ actions, children, onClose, open, title }) {
  if (!open) return null;
  return (
    <>
      <div style={styles.drawerBackdrop} onClick={onClose} />
      <div aria-modal="true" role="dialog" style={styles.modal}>
        <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 12 }}>{title}</h2>
          <button aria-label="Fechar" onClick={onClose} style={styles.iconButton} type="button">
            <CloseIcon size="small" />
          </button>
        </div>
        <div style={styles.modalBody}>{children}</div>
        {actions ? <div style={styles.modalActions}>{actions}</div> : null}
      </div>
    </>
  );
}

Modal.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
};

export function Drawer({ children, onClose, open, subtitle, title }) {
  if (!open) return null;
  return (
    <>
      <div style={styles.drawerBackdrop} onClick={onClose} />
      <aside aria-modal="true" role="dialog" style={styles.drawer}>
        <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <div>
            <h2 style={styles.sectionTitle}>{title}</h2>
            {subtitle ? <p style={{ ...styles.caption, marginTop: 4 }}>{subtitle}</p> : null}
          </div>
          <button aria-label="Fechar" onClick={onClose} style={styles.iconButton} type="button">
            <CloseIcon size="small" />
          </button>
        </div>
        <div style={{ display: 'grid', gap: 16, marginTop: 20 }}>{children}</div>
      </aside>
    </>
  );
}

Drawer.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  subtitle: PropTypes.node,
  title: PropTypes.node.isRequired,
};

export function Stepper({ steps }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {steps.map((step, index) => (
        <div
          key={step.id}
          style={{
            alignItems: 'start',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            display: 'grid',
            gap: 12,
            gridTemplateColumns: '34px minmax(0, 1fr) auto',
            padding: 14,
          }}
        >
          <IconBox active={step.status === 'PENDING'}>{index + 1}</IconBox>
          <div>
            <strong>{step.title}</strong>
            <p style={{ ...styles.caption, marginTop: 4 }}>{step.description}</p>
          </div>
          <Tag kind={step.status === 'ATTENTION' ? 'warning' : step.status === 'DISABLED' ? 'neutral' : 'default'}>
            {step.status}
          </Tag>
        </div>
      ))}
    </div>
  );
}

Stepper.propTypes = {
  steps: PropTypes.array.isRequired,
};

export function UploadList({ files, limit = 20 }) {
  return (
    <Card style={{ display: 'grid', gap: 12 }}>
      <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <strong>Fotos e documentos</strong>
          <p style={{ ...styles.caption, marginTop: 4 }}>{files.length}/{limit} arquivos enviados</p>
        </div>
        <ButtonLink>Adicionar arquivos</ButtonLink>
      </div>
      {files.map((file) => (
        <div
          key={file.id}
          style={{
            alignItems: 'center',
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            display: 'grid',
            gap: 12,
            gridTemplateColumns: '52px minmax(0, 1fr)',
            padding: 10,
          }}
        >
          <IconBox>{file.type}</IconBox>
          <div>
            <strong style={{ overflowWrap: 'anywhere' }}>{file.name}</strong>
            <div style={{ alignItems: 'center', display: 'grid', gap: 8, gridTemplateColumns: 'minmax(0, 1fr) auto', marginTop: 8 }}>
              <span style={{ background: colors.surfaceAlt, borderRadius: 999, display: 'block', height: 8, overflow: 'hidden' }}>
                <span style={{ background: colors.success, display: 'block', height: '100%', width: `${file.progress}%` }} />
              </span>
              <span style={{ color: colors.muted, fontSize: 12 }}>{file.progress}%</span>
            </div>
          </div>
        </div>
      ))}
    </Card>
  );
}

UploadList.propTypes = {
  files: PropTypes.array.isRequired,
  limit: PropTypes.number,
};

export function PriceTable({ pricing }) {
  const rows = [
    ['Aluguel', pricing.rentPrice],
    ['IPTU', pricing.houseTax / 12],
    ['Condomínio', pricing.condoPrice],
    ['Taxa de adm. (10%)', pricing.rentPrice * 0.1],
  ];
  return (
    <Card>
      <h3 style={{ ...styles.sectionTitle, fontSize: 20 }}>Valores</h3>
      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ color: colors.muted }}>{label}</span>
            <strong>{money(value)}</strong>
          </div>
        ))}
      </div>
      <hr style={styles.divider} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <strong>Proprietário vai receber</strong>
        <strong>{money(pricing.ownerEarns)}</strong>
      </div>
    </Card>
  );
}

PriceTable.propTypes = {
  pricing: PropTypes.object.isRequired,
};

export function SearchBox({ onSearch, placeholder = 'Buscar por endereço ou código' }) {
  const [value, setValue] = useState('');
  const submit = () => {
    if (value.trim() && onSearch) onSearch(value.trim());
  };
  return (
    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
      <input
        aria-label="Buscar"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit();
        }}
        placeholder={placeholder}
        style={styles.field}
        value={value}
      />
      <ButtonLink onClick={submit}>Buscar</ButtonLink>
    </div>
  );
}

SearchBox.propTypes = {
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
};

export function Timeline({ items }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {items.map((item, index) => (
        <div key={item.title} style={{ display: 'grid', gap: 12, gridTemplateColumns: '34px minmax(0, 1fr)' }}>
          <IconBox active={index === 0}>{index + 1}</IconBox>
          <Card padding={14}>
            <strong>{item.title}</strong>
            <p style={{ ...styles.caption, marginTop: 4 }}>{item.description}</p>
          </Card>
        </div>
      ))}
    </div>
  );
}

Timeline.propTypes = {
  items: PropTypes.array.isRequired,
};

export function DetailList({ rows }) {
  return (
    <dl style={{ display: 'grid', gap: 10, margin: 0 }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'grid', gap: 4 }}>
          <dt style={{ color: colors.muted, fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>{label}</dt>
          <dd style={{ margin: 0 }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

DetailList.propTypes = {
  rows: PropTypes.array.isRequired,
};
